import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Mail, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AdminTitle } from "./AdminLayout";
import { Confirm } from "./Confirm";
import { http, apiError, formatDateTime, telHref } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { INQ_STATUS } from "./inquiryUi";

const STATUS = { new: "New", in_progress: "In progress", closed: "Closed" };
const INQ_FIELDS = [["Asset", "asset_title"], ["Stock #", "stock_number"], ["Division", "division"], ["Topic", "topic"], ["Alert sent to", "routed_to"], ["Message", "message"]];
const SRC_FIELDS = [["Division", "division"], ["Asset type", "asset_type"], ["Make", "make"], ["Model", "model"], ["Year", "year_range"], ["Budget", "budget"],
  ["Condition", "condition"], ["Location", "location"], ["Timeline", "timeline"], ["Requirements", "requirements"]];

const Row = ({ item, fields, endpoint, onChange, canDelete }) => {
  const [open, setOpen] = useState(item.status === "new");
  const [notes, setNotes] = useState(item.notes || "");
  const update = async (body) => {
    try {
      await http.patch(`${endpoint}/${item.id}`, { status: item.status, ...body });
      toast.success("Updated");
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const del = async () => {
    await http.delete(`${endpoint}/${item.id}`).catch((e) => toast.error(apiError(e)));
    onChange();
  };
  const title = item.asset_title || [item.division, item.asset_type].filter(Boolean).join(" · ") || "General request";
  return (
    <div className="border hairline bg-[var(--ink-2)]" data-testid={`request-row-${item.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-col md:flex-row md:items-center gap-2 md:gap-6 p-5 text-left">
        <span className={`font-mono text-[0.65rem] uppercase px-2 py-1 border self-start ${item.status === "new" && !item.inquiry_id ? "border-[var(--gold)] text-gold" : item.status === "closed" ? "border-slate-500/50 text-slate-400" : "border-sky-400/40 text-sky-300"}`}>{item.inquiry_id ? (INQ_STATUS[item.inquiry_status]?.label || "In inquiries") : STATUS[item.status]}</span>
        <span className="flex-1 min-w-0"><span className="block text-slate-100 font-medium truncate">{item.name} — {title}</span><span className="block text-xs text-slate-400 mt-1">{item.ref ? `${item.ref} · ` : ""}{formatDateTime(item.created_at)}{item.inquiry_id ? ` · ${item.assigned_name || "Unassigned"}` : ""}{item.email_status === "failed" ? " · email alert failed" : ""}</span></span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t hairline p-5 grid lg:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-gold hover:underline break-all"><Mail className="w-4 h-4 shrink-0" /> {item.email}</a>
            {item.phone && <a href={telHref(item.phone)} className="flex items-center gap-2 text-slate-200 hover:text-gold"><Phone className="w-4 h-4" /> {item.phone}</a>}
            <dl className="divide-y divide-white/10 border-t hairline mt-3">
              {fields.filter(([, k]) => item[k]).map(([l, k]) => (
                <div key={k} className="grid grid-cols-[120px_1fr] gap-3 py-2"><dt className="text-slate-400">{l}</dt><dd className="text-slate-100 whitespace-pre-line break-words">{item[k]}</dd></div>
              ))}
            </dl>
          </div>
          {item.inquiry_id ? (
            <div className="space-y-3" data-testid={`request-managed-${item.id}`}>
              <p className="text-sm text-slate-300">This request is managed in Inquiries{item.ref ? <> as <span className="font-mono text-gold">{item.ref}</span></> : null}: assign it, schedule follow-ups and log customer contact there.</p>
              <Link to={`/admin/inquiries?id=${item.inquiry_id}`} className="btn-gold !py-2.5 !px-4 !text-xs" data-testid={`request-open-inquiry-${item.id}`}>Manage in Inquiries <ArrowRight className="w-3 h-3" /></Link>
            </div>
          ) : (
          <div className="space-y-3">
            <label className="label">Status</label>
            <select className="field" value={item.status} onChange={(e) => update({ status: e.target.value })} data-testid={`request-status-${item.id}`}>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="label">Private notes</label>
            <textarea rows={3} className="field" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`request-notes-${item.id}`} />
            <div className="flex gap-3">
              <button onClick={() => update({ notes })} className="btn-ghost !py-2 !px-4 !text-xs" data-testid={`request-save-notes-${item.id}`}>Save notes</button>
              {canDelete && <Confirm title="Delete this record?" text="This cannot be undone." onConfirm={del}>
                <button className="btn-ghost !py-2 !px-4 !text-xs hover:!border-rose-400 hover:!text-rose-300" data-testid={`request-delete-${item.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
              </Confirm>}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const RequestsList = ({ kind }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const endpoint = kind === "inquiries" ? "/admin/inquiries" : "/admin/sourcing-requests";
  const { data, isLoading } = useQuery({ queryKey: [endpoint], queryFn: async () => (await http.get(endpoint)).data });
  const [filter, setFilter] = useState("");
  const items = (data?.items || []).filter((i) => !filter || i.status === filter);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: [endpoint] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };
  return (
    <div data-testid={`admin-${kind}`}>
      <AdminTitle title={kind === "inquiries" ? "Customer inquiries" : "Sourcing requests"} sub={kind === "inquiries" ? "General inquiries alert the info address; asset and sales inquiries alert the sales address." : "New requests alert the sales address and send the customer a confirmation."}>
        <select className="field !w-auto !py-2.5" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`${kind}-status-filter`}>
          <option value="">All</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </AdminTitle>
      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <p className="border border-dashed border-white/20 p-12 text-center text-slate-300">Nothing here yet.</p>
      ) : (
        <div className="space-y-3">{items.map((i) => <Row key={`${i.id}-${i.status}`} item={i} fields={kind === "inquiries" ? INQ_FIELDS : SRC_FIELDS} endpoint={endpoint} onChange={refresh} canDelete={user?.role === "owner"} />)}</div>
      )}
    </div>
  );
};

export const InquiriesAdmin = () => <RequestsList kind="inquiries" />;
export const SourcingAdmin = () => <RequestsList kind="sourcing" />;
